import { SpaceKind, SpaceNodeKind, TreeSpaceKind } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';

export const SPACES_STALE_TIME_MS = 30 * 1000;

// Each space owns its tree routes, so either can guard or extend them; the server logic is shared.
export const SPACE_BACKEND_ROUTE: Record<TreeSpaceKind, BackendRoute> = {
  [SpaceKind.DATA_ROOM]: BackendRoute.DATA_ROOMS,
  [SpaceKind.WORKSPACE]: BackendRoute.WORKSPACES,
};

// Where a folder or a file sits under its space's routes, for the writes that address one of them.
export const NODE_ROUTE_SEGMENT: Record<SpaceNodeKind, string> = {
  [SpaceNodeKind.FILE]: 'files',
  [SpaceNodeKind.FOLDER]: 'folders',
};

// A folder change reaches its folder's contents, search results, the sidebar roots and the card
// counts; the sidebar's folder levels are refreshed for the changed space alone
// (`invalidateSpaceFolderLevels`).
export const FOLDER_INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_SPACE_FOLDER_CONTENTS,
  QueryKey.GET_SPACE_SEARCH,
  QueryKey.GET_SPACES_HOME,
  QueryKey.GET_SPACES_NAVIGATION,
];

// A file changes only its folder's contents, search results and the counts on the cards, never the
// sidebar tree.
export const FILE_INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_SPACE_FOLDER_CONTENTS,
  QueryKey.GET_SPACE_SEARCH,
  QueryKey.GET_SPACES_HOME,
];

// A Vault file changes its folder, the counts on the Vault's top level, search results and the
// cards — and the events it may be a document of, which the Vault page refreshes too.
export const VAULT_FILE_INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_VAULT_FOLDER,
  QueryKey.GET_HARDCODED_REPOSITORY,
  QueryKey.GET_EVENT,
  QueryKey.GET_EVENT_TRANSACTIONS,
  QueryKey.GET_EVENT_TRANSACTIONS_COUNT,
  ...FILE_INVALIDATION_KEYS,
];

// Every workspace write refreshes Home, the sidebar roots and the detail.
export const WORKSPACE_INVALIDATION_KEYS: QueryKey[] = [
  QueryKey.GET_SPACES_HOME,
  QueryKey.GET_SPACES_NAVIGATION,
  QueryKey.GET_WORKSPACE,
];

/**
 * What a write made somewhere else leaves out of date here: everything the module reads of the
 * spaces, both halves of it, the recipient's rooms as well as the owner's tree.
 *
 * Wider than any one write's own list, because the server says only that something changed — the
 * one thing it can say without deciding here, a second time, who may see what. Each key refreshes
 * through the route that scopes it, so a person told about a change that is not theirs sees what
 * they saw before. The Vault is not here: its files change through its own routes.
 */
/**
 * How long the pushes are gathered up before the reads are refreshed.
 *
 * One upload of twenty files is twenty writes, and each one is announced. Without this every tab
 * watching the folder would re-read it twenty times over; with it they read it once, a moment
 * after the last file lands.
 */
export const SPACES_LIVE_DEBOUNCE_MS = 400;

export const SPACES_LIVE_QUERY_KEYS: QueryKey[] = [
  QueryKey.DATA_ROOM,
  QueryKey.DATA_ROOM_ACCESS,
  QueryKey.GET_SPACE_FOLDER_CONTENTS,
  QueryKey.GET_SPACE_NAVIGATION_FOLDERS,
  QueryKey.GET_SPACE_SEARCH,
  QueryKey.GET_SPACES_HOME,
  QueryKey.GET_SPACES_NAVIGATION,
  QueryKey.GET_WORKSPACE,
  QueryKey.MY_DATA_ROOMS,
  QueryKey.MY_DATA_ROOMS_NAVIGATION,
];
